import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Settings → Credential Templates. Edits the three predefined SFTP
// credential templates (Esso / PetroCan / Passport). Saving here updates
// the defaults applied when that type is selected on any site — so a
// provider's SFTP server change is a one-place edit.

const FIELDS = [
  { key: 'name',              label: 'Name' },
  { key: 'host',              label: 'Host',                   mono: true },
  { key: 'port',              label: 'Port',                   mono: true, type: 'number' },
  { key: 'default_username',  label: 'Default Username',       mono: true, hint: 'Use [site_number] to insert the site’s number, e.g. [site_number]-bk' },
  { key: 'default_password',  label: 'Default Password',       mono: true, hint: 'Leave blank for per-site passwords; set it when every site shares one (e.g. PetroCan)' },
  { key: 'source_dir',        label: 'Source Directory',       mono: true },
  { key: 'destination_dir',   label: 'Destination Directory',  mono: true },
  { key: 'file_type_support', label: 'File Type Support' },
  { key: 'sftp_run_time',     label: 'SFTP Run Time' }
];

function TemplateCard({ template, onSaved }) {
  const [form, setForm] = useState(() => ({ ...template }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setForm({ ...template }); }, [template]);

  function update(key) {
    return (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  async function save() {
    setSaving(true); setError(null); setMsg(null);
    try {
      const payload = {};
      for (const f of FIELDS) payload[f.key] = form[f.key] ?? '';
      const res = await axios.put(`/api/credential-templates/${template.key}`, payload);
      setMsg('Saved.');
      if (onSaved) onSaved(res.data.template);
      setTimeout(() => setMsg(null), 2500);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settings-card">
      <header className="settings-card-head">
        <h3>{template.name}</h3>
        <p className="settings-card-sub">
          Defaults applied when <strong>{template.name}</strong> is selected as a credential type on a site.
        </p>
      </header>
      <div className="form-grid">
        {FIELDS.map((f) => (
          <label key={f.key} className={`form-field${f.key === 'default_username' || f.key === 'host' ? ' form-field-wide' : ''}`}>
            <span>{f.label}</span>
            <input
              type={f.type || 'text'}
              className={f.mono ? 'cell-mono' : undefined}
              value={form[f.key] ?? ''}
              onChange={update(f.key)}
              autoComplete="off"
            />
            {f.hint && <span className="form-hint">{f.hint}</span>}
          </label>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
      {msg && <p className="save-message">{msg}</p>}
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : `Save ${template.name} Template`}
        </button>
      </div>
    </section>
  );
}

export default function CredentialTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get('/api/credential-templates');
      setTemplates(res.data.templates || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <section className="page">
      <div className="page-header">
        <Link to="/settings" className="btn-secondary">← Back to Settings</Link>
      </div>
      <div className="record-header" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="record-title" style={{ flex: 1 }}>
          <h2>Credential Templates</h2>
          <div className="record-meta">
            Edit the SFTP defaults for each provider. Changes apply the next time the type is selected on a site.
          </div>
        </div>
      </div>

      {loading && <p className="modal-sub">Loading…</p>}
      {error && <p className="error">{error}</p>}

      {templates.map((t) => (
        <TemplateCard
          key={t.key}
          template={t}
          onSaved={(updated) => setTemplates((prev) => prev.map((x) => x.key === updated.key ? updated : x))}
        />
      ))}
    </section>
  );
}
